import * as ex from 'excalibur';
import { ActionType, Allegiance, ControlType } from '../types/enums';
import { Action } from '../types/interfaces';
import { ANIMATION_DURATION, TILE_SIZE, DIRECTIONS } from '../config/constants';
import { Unit } from '../core/unit';
import { Grid } from '../core/grid';

// Player-controlled Unit base class
export class PlayerUnit extends Unit {
  private moveDirection: { x: number, y: number } | null = null;
  private keyHandlers: Map<ex.Input.Keys, { x: number, y: number }>;
  private inputEnabled: boolean = true;

  constructor(options: {
    gridX: number,
    gridY: number,
    type: string,
    name: string,
    color: ex.Color,
    initiative: number,
    health: number,
    maxHealth: number,
    moveRange?: number,
    attackRange?: number,
    abilities?: string[],
    grid: Grid
  }) {
    super({
      pos: new ex.Vector(options.gridX * TILE_SIZE + TILE_SIZE / 2, options.gridY * TILE_SIZE + TILE_SIZE / 2),
      gridX: options.gridX,
      gridY: options.gridY,
      type: options.type,
      name: options.name,
      width: TILE_SIZE * 0.8,
      height: TILE_SIZE * 0.8,
      color: options.color,
      initiative: options.initiative,
      allegiance: Allegiance.Player,
      health: options.health,
      maxHealth: options.maxHealth,
      moveRange: options.moveRange,
      attackRange: options.attackRange,
      abilities: options.abilities,
      grid: options.grid
    });

    // Setup key handlers
    this.keyHandlers = new Map([
      [ex.Input.Keys.Up, DIRECTIONS.UP],
      [ex.Input.Keys.Down, DIRECTIONS.DOWN],
      [ex.Input.Keys.Left, DIRECTIONS.LEFT],
      [ex.Input.Keys.Right, DIRECTIONS.RIGHT],
      // WASD support
      [ex.Input.Keys.W, DIRECTIONS.UP],
      [ex.Input.Keys.S, DIRECTIONS.DOWN],
      [ex.Input.Keys.A, DIRECTIONS.LEFT],
      [ex.Input.Keys.D, DIRECTIONS.RIGHT],
      // Attack key - spacebar
      [ex.Input.Keys.Space, { x: 0, y: 0 }] // Special value for attack
    ]);

    // Use the actor's own update event instead of scene
    this.on('postupdate', () => {
      // Only process input if player has action points and it's their turn
      if (this.inputEnabled && this.actionPoints > 0 && this.grid.game.currentActor === this) {
        this.processInput();
      }
    });
  }

  private processInput() {
    // Process all keyboard inputs
    const engine = this.scene?.engine;
    if (!engine) return;

    for (const [key, direction] of this.keyHandlers.entries()) {
      if (engine.input.keyboard.wasPressed(key)) {
        // Special case for space key (attack)
        if (key === ex.Input.Keys.Space) {
          // Find enemies in attack range
          const targets = this.findTargetsInRange(this.attackRange);
          if (targets.length > 0) {
            // Attack the first enemy in range
            const target = targets[0];
            this.moveDirection = null; // Clear move direction

            // Disable input during attack
            this.inputEnabled = false;
            setTimeout(() => {
              this.inputEnabled = true;
            }, 200); // Re-enable after attack animation

            return; // Exit after setting up attack
          }
        } else {
          // Normal movement direction
          this.moveDirection = direction;
          return; // Exit as soon as we process a key
        }
      }
    }
  }

  public decideAction(): Action | null {
    // If space was pressed and we have a target, attack
    const targets = this.findTargetsInRange(this.attackRange);
    if (targets.length > 0 && !this.moveDirection) {
      return this.createAttackAction(targets[0]);
    }

    // Otherwise handle movement
    if (this.moveDirection) {
      const targetX = this.gridX + this.moveDirection.x;
      const targetY = this.gridY + this.moveDirection.y;

      // Check if move is valid
      if (this.grid.isValidMove(targetX, targetY)) {
        // Temporarily disable input while action is executing
        this.inputEnabled = false;

        const moveAction = this.createMoveAction(targetX, targetY);

        // Extend the execute method to handle player-specific logic
        const originalExecute = moveAction.execute;
        moveAction.execute = () => {
          originalExecute();
          this.moveDirection = null;

          // Re-enable input after the animation is complete
          setTimeout(() => {
            this.inputEnabled = true;
          }, ANIMATION_DURATION * 0.8); // Slightly before animation ends
        };

        return moveAction;
      }

      // Reset move direction if invalid
      this.moveDirection = null;
    }

    // If no valid move, return null to indicate waiting for player input
    return null;
  }
}